import { useState } from "react";
import type { LaunchPerpFormState } from "../types/perp";
import "./LaunchPerpForm.css";

/** Default tick size in satoshi (1e8 = 1.0). */
const DEFAULT_TICK_SIZE_SATOSHI = "100000000";
/** Default min order size (0.001 in satoshi: 100000). */
const DEFAULT_MIN_ORDER_SIZE = "100000";
/** Default max order size (1e8 = 1.0 in satoshi). */
const DEFAULT_MAX_ORDER_SIZE = "100000000";

interface LaunchPerpFormProps {
  initial?: Partial<LaunchPerpFormState>;
  onSubmit: (form: LaunchPerpFormState) => void;
  onBack: () => void;
}

export function LaunchPerpForm({
  initial,
  onSubmit,
  onBack,
}: LaunchPerpFormProps) {
  const [baseAsset, setBaseAsset] = useState(initial?.baseAsset ?? "BTC");
  const [quoteAsset, setQuoteAsset] = useState(initial?.quoteAsset ?? "USDC");
  const [tickSizeSatoshi, setTickSizeSatoshi] = useState(
    initial?.tickSizeSatoshi ?? DEFAULT_TICK_SIZE_SATOSHI
  );
  const [minOrderSize, setMinOrderSize] = useState(
    initial?.minOrderSize ?? DEFAULT_MIN_ORDER_SIZE
  );
  const [maxOrderSize, setMaxOrderSize] = useState(
    initial?.maxOrderSize ?? DEFAULT_MAX_ORDER_SIZE
  );
  const [oracleId, setOracleId] = useState(initial?.oracleId ?? "");
  const [activateImmediately, setActivateImmediately] = useState(
    initial?.activateImmediately ?? true
  );

  const derivedSymbol = `${baseAsset}-${quoteAsset}-PERP`;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      baseAsset: baseAsset.trim().toUpperCase(),
      quoteAsset: quoteAsset.trim().toUpperCase(),
      tickSizeSatoshi: tickSizeSatoshi.trim() || DEFAULT_TICK_SIZE_SATOSHI,
      minOrderSize: minOrderSize.trim() || DEFAULT_MIN_ORDER_SIZE,
      maxOrderSize: maxOrderSize.trim() || DEFAULT_MAX_ORDER_SIZE,
      ...(oracleId.trim() ? { oracleId: oracleId.trim() } : {}),
      activateImmediately,
    });
  };

  return (
    <div className="launch-perp-form screen">
      <header className="form-header">
        <button type="button" className="back-btn" onClick={onBack}>
          ← Back
        </button>
        <h1>Launch Perp Market</h1>
      </header>

      <form className="launch-perp-form-inner" onSubmit={handleSubmit}>
        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="perp-base-asset">Base Asset</label>
            <input
              id="perp-base-asset"
              type="text"
              value={baseAsset}
              onChange={(e) => setBaseAsset(e.target.value)}
              placeholder="e.g. BTC"
            />
          </div>
          <div className="form-group">
            <label htmlFor="perp-quote-asset">Quote Asset</label>
            <input
              id="perp-quote-asset"
              type="text"
              value={quoteAsset}
              onChange={(e) => setQuoteAsset(e.target.value)}
              placeholder="e.g. USDC"
            />
          </div>
        </div>

        <p className="launch-perp-hint">
          The market will be created as a perpetual contract. Symbol format:{" "}
          <strong>{derivedSymbol}</strong>
        </p>

        <div className="form-group">
          <label htmlFor="perp-tick-size">Tick Size (satoshi)</label>
          <input
            id="perp-tick-size"
            type="text"
            value={tickSizeSatoshi}
            onChange={(e) => setTickSizeSatoshi(e.target.value)}
            placeholder="e.g. 100000000 (= 1.0)"
          />
          <p className="field-hint">
            Min price increment. 1e8 = 1.0 (e.g. 100000000 for $1 tick).
          </p>
        </div>

        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="perp-min-order">Min Order Size (satoshi)</label>
            <input
              id="perp-min-order"
              type="text"
              value={minOrderSize}
              onChange={(e) => setMinOrderSize(e.target.value)}
              placeholder="e.g. 100000"
            />
          </div>
          <div className="form-group">
            <label htmlFor="perp-max-order">Max Order Size (satoshi)</label>
            <input
              id="perp-max-order"
              type="text"
              value={maxOrderSize}
              onChange={(e) => setMaxOrderSize(e.target.value)}
              placeholder="e.g. 100000000"
            />
          </div>
        </div>

        <div className="form-group oracle-group">
          <label htmlFor="perp-oracle-id">Oracle ID (optional)</label>
          <input
            id="perp-oracle-id"
            type="text"
            value={oracleId}
            onChange={(e) => setOracleId(e.target.value)}
            placeholder="e.g. pyth:0x... or chainlink:0x..."
          />
          <p className="field-hint">
            Price feed identifier for index/mark price. Leave empty to use
            system default for this pair.
          </p>
        </div>

        <div className="form-group checkbox-group">
          <label>
            <input
              type="checkbox"
              checked={activateImmediately}
              onChange={(e) => setActivateImmediately(e.target.checked)}
            />
            Activate market immediately after creation
          </label>
          <p className="field-hint">
            If unchecked, the market will be created in pending state and must be
            activated separately before trading.
          </p>
        </div>

        <div className="form-preview">
          <p>
            New perpetual: <strong>{derivedSymbol}</strong> · Tick size:{" "}
            {tickSizeSatoshi || "—"} satoshi · Min / Max size: {minOrderSize}{" "}
            / {maxOrderSize}
          </p>
        </div>

        <div className="form-actions">
          <button type="button" className="secondary" onClick={onBack}>
            Cancel
          </button>
          <button type="submit" className="primary">
            Submit Proposal
          </button>
        </div>
      </form>
    </div>
  );
}
